import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'
import { setQty } from '../lib/cart'
import { Bot, Upload, Globe, FileText, MessageSquare, Code, Play, Settings, Trash2, X, Layers, Loader2 } from 'lucide-react'
import { getSession, authHeaders } from '../lib/session'
import ProductGuide from '../components/ProductGuide'
import AgentAsk from '../components/AgentAsk'
import ContentSource from '../components/ContentSource'
import KnowledgeView from '../components/KnowledgeView'

const LEAD_FIELDS = ['slackWebhookUrl', 'notifyEmail', 'webhookUrl', 'telegramBotToken', 'telegramChatId', 'discordWebhookUrl', 'hubspotToken', 'twilioSid', 'twilioToken', 'twilioWhatsappFrom', 'whatsappTo']
const SECRET_FIELDS = ['slackWebhookUrl', 'discordWebhookUrl', 'webhookUrl', 'telegramBotToken', 'hubspotToken', 'twilioToken']

export default function ChatbotManager() {
  const session = getSession()
  const navigate = useNavigate()
  const [instances, setInstances] = useState([])
  const [botStats, setBotStats] = useState({})
  useEffect(() => { if (session?.namespaceId) axios.get(`/api/v1/tenants/${session.namespaceId}/stats`).then(r => setBotStats(r.data)).catch(() => {}) }, [])
  const [leads, setLeads] = useState([])
  const [activeTab, setActiveTab] = useState('instances')
  const [selectedInstance, setSelectedInstance] = useState(null)
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const chatEndRef = useRef(null)

  const [configForm, setConfigForm] = useState({
    systemPrompt: 'You are a helpful AI assistant for our company.',
    persona: 'professional',
    model: 'gpt-4.1',
    temperature: 0.7,
    enableEscalation: true,
    enableLeadCapture: true,
    icebreakers: ['What services do you offer?', 'How do I get started?', 'Pricing information']
  })

  const loadInstances = () => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/products`).then(res => {
      const list = (res.data.instances || []).filter(i => i.product_type === 'answers' && i.status === 'active')
      setInstances(list)
      // Auto-select the first bot so Train/Chat/Embed aren't blank on arrival.
      setSelectedInstance(prev => prev || list[0] || null)
    }).catch(err => {
      toast.error(err.response?.data?.error || 'Failed to load chatbots')
    })
  }

  useEffect(loadInstances, [])

  const loadLeads = () => {
    if (!session?.namespaceId) return
    axios.get('/api/v1/answers/handoffs').then(res => setLeads(res.data.handoffs || [])).catch(() => {})
  }
  useEffect(loadLeads, [])

  // Data-source instances — a new chatbot runs on one; new source → new instance.
  const [dataSources, setDataSources] = useState([])
  const loadDataSources = () => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/instances`).then(r => setDataSources(r.data.instances || [])).catch(() => {})
  }
  useEffect(loadDataSources, [])
  const [newBot, setNewBot] = useState(null) // { name, mode:'existing'|'new', instanceId, sourceName, sourceUrl, busy }

  // Channels — where the bot answers besides the website (Slack, WhatsApp).
  const [channels, setChannels] = useState([])
  const [chForm, setChForm] = useState({ slackBotToken: '', slackSigningSecret: '', waPhoneNumberId: '', waAccessToken: '', waVerifyToken: '' })
  const [chBusy, setChBusy] = useState('')
  const loadChannels = () => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/channels`).then(r => setChannels(r.data.channels || [])).catch(() => {})
  }
  useEffect(loadChannels, [])
  const saveChannel = async (channel) => {
    setChBusy(channel)
    try {
      const body = channel === 'slack'
        ? { botToken: chForm.slackBotToken.trim(), signingSecret: chForm.slackSigningSecret.trim() || undefined }
        : { phoneNumberId: chForm.waPhoneNumberId.trim(), accessToken: chForm.waAccessToken.trim(), verifyToken: chForm.waVerifyToken.trim() || undefined }
      const r = await axios.post(`/api/v1/tenants/${session.namespaceId}/channels/${channel}`, body, { validateStatus: s => s < 500 })
      if (r.status >= 400) { toast.error(r.data.error || 'Could not connect'); return }
      toast.success(r.data.message || `${channel} connected`)
      loadChannels()
    } catch (err) { toast.error(err.response?.data?.error || 'Failed') } finally { setChBusy('') }
  }

  const [settings, setSettings] = useState(() => ({ customInstructions: '', ...Object.fromEntries(LEAD_FIELDS.map(k => [k, ''])) }))
  const [savingSettings, setSavingSettings] = useState(false)
  const loadSettings = () => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/bot/settings`).then(res => {
      const s = { customInstructions: res.data.customInstructions || '', configured: res.data.configured || {} }
      LEAD_FIELDS.forEach(k => { s[k] = res.data[k] || '' })
      setSettings(s)
    }).catch(() => {})
  }
  const setField = (k, v) => setSettings(s => ({ ...s, [k]: v }))
  // Secret fields come back blank; show a "saved" hint so users know to leave them.
  const ph = (k, base) => (SECRET_FIELDS.includes(k) && settings.configured?.[k]) ? '•••••••• saved — leave blank to keep' : base
  useEffect(loadSettings, [])
  const saveSettings = async () => {
    if (!session?.namespaceId) return
    setSavingSettings(true)
    try { await axios.put(`/api/v1/tenants/${session.namespaceId}/bot/settings`, settings); toast.success('Settings saved') }
    catch (err) { toast.error(err.response?.data?.error || 'Save failed') }
    finally { setSavingSettings(false) }
  }

  // Allowed embed domains — the widget only answers on these sites (empty = any).
  // Loads the current lock so it shows what's set; defaults to the site domain.
  const [domainsText, setDomainsText] = useState('')
  const [savingDomains, setSavingDomains] = useState(false)
  const loadDomains = () => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/bot/domains`)
      .then(res => setDomainsText((res.data.domains || []).join(', ')))
      .catch(() => {}) // 404 when Answers isn't activated yet — just show the empty field
  }
  useEffect(loadDomains, [])
  const saveDomains = async () => {
    if (!session?.namespaceId) return
    setSavingDomains(true)
    try {
      const domains = domainsText.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)
      const res = await axios.put(`/api/v1/tenants/${session.namespaceId}/bot/domains`, { domains }, { validateStatus: s => s < 500 })
      if (res.status >= 400) { toast.error(res.data.error || 'Could not save domains'); return }
      setDomainsText((res.data.domains || []).join(', '))
      toast.success(domains.length ? 'Domain lock saved' : 'Domain lock cleared — bot now answers on any site')
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed') }
    finally { setSavingDomains(false) }
  }

  // Strip a URL to its bare hostname — mirrors the backend normalizeDomain().
  // Used to lock a new bot's embed widget to the customer's own site by default.
  const domainOf = (url) => {
    if (!url) return null
    let s = String(url).trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '')
    s = s.split('/')[0].split('?')[0].split('#')[0].split(':')[0]
    return s || null
  }

  const createBot = (name, dataInstanceId, config) => axios.post(`/api/v1/tenants/${session.namespaceId}/products`,
    { productType: 'answers', instanceName: name, instanceId: dataInstanceId, config: config || {} }, { validateStatus: s => s < 500 })

  const handleNewChatbot = () => {
    if (!session?.namespaceId) return
    setNewBot({
      name: 'Support Bot',
      mode: dataSources.length ? 'existing' : 'new',
      instanceId: (dataSources.find(i => i.isDefault) || dataSources[0])?.id || '',
      sourceName: '', sourceUrl: '', busy: false,
    })
  }

  const submitNewBot = async () => {
    const nb = newBot
    if (!nb.name.trim()) { toast.error('Name your chatbot'); return }
    setNewBot(b => ({ ...b, busy: true }))
    try {
      // New source → create the instance first, then put the bot on it.
      let dataInstanceId = nb.instanceId
      if (nb.mode === 'new') {
        const ci = await axios.post(`/api/v1/tenants/${session.namespaceId}/instances`,
          { name: nb.sourceName.trim() || nb.name.trim(), sourceUrl: nb.sourceUrl.trim() || undefined },
          { validateStatus: s => s < 500 })
        if (ci.status >= 400) { toast.error(ci.data.error || 'Could not create the source'); setNewBot(b => ({ ...b, busy: false })); return }
        dataInstanceId = ci.data.instance.id
      }

      // Secure-by-default: lock the widget to the site it's trained on. New source →
      // the URL just entered; existing source → that instance's saved sourceUrl.
      // Empty (no URL) → no lock, same as before. The customer can edit this later
      // under Settings → Allowed website domains.
      const seedUrl = nb.mode === 'new'
        ? nb.sourceUrl.trim()
        : (dataSources.find(i => i.id === dataInstanceId)?.sourceUrl || '')
      const seed = domainOf(seedUrl)
      const botConfig = seed ? { allowedDomains: [seed], sourceUrl: seedUrl } : {}

      let res = await createBot(nb.name.trim(), dataInstanceId, botConfig)
      if (res.status === 403 && res.data.code === 'NOT_ACTIVATED') {
        toast.error('Add the AI Support Chatbot service first — see the Services page.'); setNewBot(b => ({ ...b, busy: false })); return
      }
      if (res.status === 403 && res.data.code === 'SEATS_EXCEEDED') {
        // Buying another chatbot is a purchase → go through checkout, then come back to create it.
        setQty('answers', (res.data.seats || 1) + 1)
        toast('You’re at your paid chatbot limit — checkout to add another seat.', { icon: '🛒' })
        navigate('/checkout')
        setNewBot(b => ({ ...b, busy: false }))
        return
      }
      if (res.status >= 400) { toast.error(res.data.error || 'Failed to create chatbot'); setNewBot(b => ({ ...b, busy: false })); return }

      toast.success('Chatbot created')
      const inst = res.data.instance
      const row = { id: inst.id, instance_name: inst.name, status: inst.status, product_type: 'answers', created_at: inst.createdAt }
      setNewBot(null)
      setInstances(prev => [...prev, row]) // chatbot list
      setSelectedInstance(row)
      loadDataSources() // a new source may have been created
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed'); setNewBot(b => ({ ...b, busy: false }))
    }
  }

  const scrollToBottom = () => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  useEffect(scrollToBottom, [chatMessages])

  const handleChat = async (e) => {
    e.preventDefault()
    if (!chatInput.trim() || !selectedInstance) return

    const userMsg = { role: 'user', content: chatInput }
    setChatMessages(prev => [...prev, userMsg])
    setChatInput('')
    setIsStreaming(true)

    let placeholderAdded = false
    try {
      // Try streaming first. fetch() bypasses the axios interceptor, so attach auth explicitly.
      const response = await fetch('/api/v1/answers/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ message: userMsg.content, instanceId: selectedInstance.id })
      })
      if (!response.ok || !response.body) throw new Error('stream unavailable')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let botContent = ''

      setChatMessages(prev => [...prev, { role: 'assistant', content: '' }])
      placeholderAdded = true

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const lines = decoder.decode(value).split('\n').filter(l => l.startsWith('data: '))
        for (const line of lines) {
          let data
          try { data = JSON.parse(line.replace('data: ', '')) } catch { continue }
          if (data.error) throw new Error(data.error)
          if (data.chunk) {
            botContent += data.chunk
            setChatMessages(prev => {
              const updated = [...prev]
              updated[updated.length - 1] = { role: 'assistant', content: botContent }
              return updated
            })
          }
        }
      }
      if (!botContent) throw new Error('empty stream')
    } catch {
      // Fallback to the reliable non-streaming endpoint.
      try {
        const res = await axios.post('/api/v1/answers/chat', {
          message: userMsg.content,
          instanceId: selectedInstance.id
        })
        const answer = res.data.message || '(no response)'
        setChatMessages(prev => placeholderAdded
          ? [...prev.slice(0, -1), { role: 'assistant', content: answer }]
          : [...prev, { role: 'assistant', content: answer }])
      } catch (err2) {
        setChatMessages(prev => placeholderAdded ? prev.slice(0, -1) : prev)
        toast.error(err2.response?.data?.error || 'Chat failed')
      }
    }
    setIsStreaming(false)
  }

  const embedCode = selectedInstance ? `<script>
  (function() {
    var s = document.createElement('script');
    s.src = 'https://reright.io/widget/${selectedInstance.id}.js';
    s.async = true;
    document.head.appendChild(s);
  })();
</script>` : ''

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">ReRight Answers</h2>
          <p className="text-gray-500 mt-1">Manage chatbot instances and training data</p>
        </div>
        <button onClick={handleNewChatbot} disabled={!session?.namespaceId}
          className="btn-primary flex items-center gap-2 disabled:opacity-50">
          <Bot size={18} /> New Chatbot
        </button>
      </div>

      <ProductGuide product="answers" collapsible defaultOpen={false} />

      <div className="max-w-2xl"><AgentAsk service="answers" agentName="Chatbot Agent" starters={['How is my chatbot doing?', 'What are visitors asking most?']} /></div>

      {!session?.namespaceId && (
        <div className="card bg-amber-500/10 border-amber-500/30 text-sm text-amber-600">
          No tenant connected. Go to <Link to="/setup" className="font-semibold underline">Setup</Link> to create or connect a tenant first.
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {['instances', 'train', 'chat', 'leads', 'embed', 'channels', 'settings'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
              activeTab === tab ? 'bg-gray-100 text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Which chatbot am I working on? (Train / Chat / Embed act on one bot) */}
      {['train', 'chat', 'embed'].includes(activeTab) && instances.length > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Working on:</span>
          <select className="input w-auto py-1.5" value={selectedInstance?.id || ''}
            onChange={e => setSelectedInstance(instances.find(i => i.id === e.target.value) || null)}>
            {instances.map(i => <option key={i.id} value={i.id}>{i.instance_name}</option>)}
          </select>
        </div>
      )}

      {/* Gated tabs need a chatbot — tell the user instead of showing nothing */}
      {['train', 'chat', 'embed'].includes(activeTab) && !selectedInstance && (
        <div className="card text-center py-12">
          <Bot className="mx-auto text-gray-300 mb-3" size={40} />
          <p className="text-gray-700 font-medium mb-1">No chatbot yet</p>
          <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
            Create a chatbot first — then you can train it on your site, test it here, and copy the embed snippet
            for your website.
          </p>
          <button onClick={handleNewChatbot} className="btn-primary">Create your first chatbot</button>
        </div>
      )}

      {/* Instances Tab */}
      {activeTab === 'instances' && instances.length === 0 && (
        <div className="card text-center py-12">
          <Bot className="mx-auto text-gray-300 mb-3" size={40} />
          <p className="text-gray-700 font-medium mb-1">No chatbots yet</p>
          <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
            A chatbot lives as a chat bubble on your website and answers visitors from your own content.
            Create one to get started.
          </p>
          <button onClick={handleNewChatbot} className="btn-primary">Create your first chatbot</button>
        </div>
      )}
      {activeTab === 'instances' && instances.length > 0 && (
        <div className="grid grid-cols-3 gap-6">
          {instances.map(inst => (
            <div key={inst.id} 
              onClick={() => setSelectedInstance(inst)}
              className={`card cursor-pointer transition-all ${selectedInstance?.id === inst.id ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}>
              <div className="flex items-center justify-between">
                <Bot className="text-primary-500" size={28} />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${inst.status === 'active' ? 'bg-green-500/15 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                  {inst.status}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-3">{inst.instance_name}</h3>
              <p className="text-xs text-gray-600 mt-1.5">{botStats.conversations ?? 0} conversations · {leads.length} leads · {botStats.passages ?? 0} passages</p>
              <p className="text-xs text-gray-400 mt-1">Created {new Date(inst.created_at).toLocaleDateString()} · ID {String(inst.id).slice(0, 8)}…</p>
            </div>
          ))}
        </div>
      )}

      {/* Train Tab */}
      {activeTab === 'train' && selectedInstance && (
        <div className="card max-w-2xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Upload size={20} /> Train “{selectedInstance.instance_name}”
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Feed it however your knowledge lives — a website URL is just one option. Paste text, upload a document,
            or connect a database, cloud bucket, or Confluence/Jira. Everything you add trains this bot.
          </p>
          <ContentSource instanceId={selectedInstance.id} />
        </div>
      )}

      {/* What this bot is trained on */}
      {activeTab === 'train' && selectedInstance && (
        <div className="max-w-2xl"><KnowledgeView compact /></div>
      )}

      {/* Chat Tab */}
      {activeTab === 'chat' && selectedInstance && (
        <div className="card h-[600px] flex flex-col">
          <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
            <Bot className="text-primary-500" size={24} />
            <div>
              <h3 className="font-semibold text-gray-900">{selectedInstance.instance_name}</h3>
              <p className="text-xs text-gray-500">Test your chatbot before deploying</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {chatMessages.length === 0 && (
              <div className="text-center text-gray-400 py-12">
                <MessageSquare size={48} className="mx-auto mb-3 opacity-50" />
                <p>Start a conversation to test your chatbot</p>
                <div className="flex flex-wrap gap-2 justify-center mt-4">
                  {configForm.icebreakers.map((q, i) => (
                    <button key={i} onClick={() => { setChatInput(q); }}
                      className="px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-600 hover:bg-gray-200 transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                  msg.role === 'user' 
                    ? 'bg-primary-600 text-white rounded-br-md' 
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}>
                  {msg.content || (msg.role === 'assistant' && isStreaming && <span className="animate-pulse">...</span>)}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={handleChat} className="pt-4 border-t border-gray-200 flex gap-2">
            <input type="text" className="input flex-1" placeholder="Type a message..."
              value={chatInput} onChange={e => setChatInput(e.target.value)} disabled={isStreaming} />
            <button type="submit" disabled={isStreaming || !chatInput.trim()}
              className="btn-primary px-6 disabled:opacity-50">
              <MessageSquare size={18} />
            </button>
          </form>
        </div>
      )}

      {/* Leads Tab */}
      {activeTab === 'leads' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Leads &amp; handoffs</h3>
              <p className="text-sm text-gray-500">Visitors who asked to be contacted and left an email. Follow up with them by email.</p>
            </div>
            <button onClick={loadLeads} className="text-sm text-gray-500 hover:text-gray-900">Refresh</button>
          </div>
          {leads.length === 0 ? (
            <p className="text-sm text-gray-400">No leads yet. When a visitor asks for a consultation or a callback and shares their email, the bot captures it here — instead of promising a handoff that never happens.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Phone</th>
                    <th className="py-2 pr-4">Location</th>
                    <th className="py-2 pr-4">Wanted</th>
                    <th className="py-2 pr-4">When</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(l => (
                    <tr key={l.id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 font-medium text-gray-900">{l.contact_name || '—'}</td>
                      <td className="py-2 pr-4 text-gray-700">{l.contact_email || '—'}</td>
                      <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">{l.contact_phone || '—'}</td>
                      <td className="py-2 pr-4 text-gray-700">{l.location || '—'}</td>
                      <td className="py-2 pr-4 text-gray-600 max-w-xs truncate">{l.topic}</td>
                      <td className="py-2 pr-4 text-gray-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Embed Tab */}
      {activeTab === 'embed' && selectedInstance && (
        <div className="card max-w-2xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Code size={20} /> Embed Code
          </h3>
          <p className="text-sm text-gray-500 mb-4">Paste this snippet into your website HTML, before the closing &lt;/body&gt; tag.</p>
          <div className="relative">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
              <code>{embedCode}</code>
            </pre>
            <button onClick={() => { navigator.clipboard.writeText(embedCode); toast.success('Copied!') }}
              className="absolute top-2 right-2 p-2 bg-gray-700 rounded hover:bg-gray-600 transition-colors">
              <Code size={16} />
            </button>
          </div>
          <div className="mt-4 p-4 bg-primary-500/10 rounded-lg">
            <p className="text-sm text-primary-600 font-medium">Hosted URL</p>
            <p className="text-sm text-primary-600 mt-1">https://reright.io/chat/{selectedInstance.id}</p>
          </div>
        </div>
      )}

      {/* Channels Tab — bot answers on Slack / WhatsApp */}
      {activeTab === 'channels' && (
        <div className="card max-w-2xl space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Answer where your customers are</h3>
            <p className="text-sm text-gray-500">Your bot already lives on your website. Connect Slack or WhatsApp and it answers there too — same knowledge, same guardrails. Activate the chatbot first; the active bot is the one that replies.</p>
          </div>

          {channels.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {channels.map(c => <span key={c.channel} className="inline-flex items-center gap-1 text-xs bg-green-500/15 text-green-700 px-2 py-1 rounded-full">{c.channel} connected</span>)}
            </div>
          )}

          {/* Slack */}
          <div className="border border-gray-200 rounded-xl p-4 space-y-2">
            <p className="font-medium text-gray-900">Slack</p>
            <p className="text-xs text-gray-500">Create a Slack app, add scopes <code className="bg-gray-100 px-1 rounded">app_mentions:read</code>, <code className="bg-gray-100 px-1 rounded">chat:write</code>, and point <b>Event Subscriptions</b> at <code className="bg-gray-100 px-1 rounded">https://ai.reright.io/api/v1/channels/slack/events</code>. Paste the Bot User OAuth token:</p>
            <input className="input" type="password" placeholder="Bot User OAuth token — xoxb-…" value={chForm.slackBotToken} onChange={e => setChForm(f => ({ ...f, slackBotToken: e.target.value }))} />
            <input className="input" type="password" placeholder="Signing secret (Basic Information → App Credentials) — verifies requests" value={chForm.slackSigningSecret} onChange={e => setChForm(f => ({ ...f, slackSigningSecret: e.target.value }))} />
            <button onClick={() => saveChannel('slack')} disabled={chBusy === 'slack' || !chForm.slackBotToken.trim()} className="btn-primary disabled:opacity-60">{chBusy === 'slack' ? 'Connecting…' : 'Connect Slack'}</button>
          </div>

          {/* WhatsApp */}
          <div className="border border-gray-200 rounded-xl p-4 space-y-2">
            <p className="font-medium text-gray-900">WhatsApp <span className="text-xs text-gray-400 font-normal">(Meta Cloud API)</span></p>
            <p className="text-xs text-gray-500">In your Meta app, set the webhook to <code className="bg-gray-100 px-1 rounded">https://ai.reright.io/api/v1/channels/whatsapp/webhook</code> with your verify token, subscribe to <code className="bg-gray-100 px-1 rounded">messages</code>, then paste:</p>
            <div className="grid sm:grid-cols-2 gap-2">
              <input className="input" placeholder="Phone number ID" value={chForm.waPhoneNumberId} onChange={e => setChForm(f => ({ ...f, waPhoneNumberId: e.target.value }))} />
              <input className="input" placeholder="Verify token (the one you set in Meta)" value={chForm.waVerifyToken} onChange={e => setChForm(f => ({ ...f, waVerifyToken: e.target.value }))} />
              <input className="input sm:col-span-2" type="password" placeholder="Permanent access token" value={chForm.waAccessToken} onChange={e => setChForm(f => ({ ...f, waAccessToken: e.target.value }))} />
            </div>
            <button onClick={() => saveChannel('whatsapp')} disabled={chBusy === 'whatsapp' || !chForm.waPhoneNumberId.trim() || !chForm.waAccessToken.trim()} className="btn-primary disabled:opacity-60">{chBusy === 'whatsapp' ? 'Connecting…' : 'Connect WhatsApp'}</button>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="card max-w-2xl space-y-6">
          {/* Allowed embed domains — the widget's domain lock (security) */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <Globe size={20} /> Allowed website domains
            </h3>
            <p className="text-sm text-gray-500 mb-3">
              Your chat widget only answers when embedded on these domains — so no one can copy your
              embed snippet onto another site and run up your message quota. New bots are locked to the
              site they’re trained on by default. Separate multiple domains with commas.
              <b> Leave empty to allow any site (not recommended).</b>
            </p>
            <input className="input"
              placeholder={(domainOf((dataSources.find(i => i.isDefault) || dataSources[0])?.sourceUrl)) ? `e.g. ${domainOf((dataSources.find(i => i.isDefault) || dataSources[0])?.sourceUrl)}` : 'e.g. yourcompany.com'}
              value={domainsText} onChange={e => setDomainsText(e.target.value)} />
            <button onClick={saveDomains} disabled={savingDomains} className="btn-primary mt-3 disabled:opacity-60">
              {savingDomains ? 'Saving…' : 'Save allowed domains'}
            </button>
          </div>

          <div className="border-t border-gray-200 pt-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <Settings size={20} /> Bot instructions &amp; guardrails
            </h3>
            <p className="text-sm text-gray-500 mb-3">
              Your bot already has built-in safety guardrails — it won't invent facts or prices, and it will never
              fake a handoff (no "connecting you now"). Add <b>your own</b> instructions on top: tone, what to
              emphasize, what to avoid, when to collect a lead. These same instructions also shape your
              <b> Sync "Ask your content"</b> answers and RAG replies — so you can tune how the AI talks about your
              business everywhere, yourself, without waiting on us.
            </p>
            <textarea className="input h-32"
              placeholder={'e.g. Speak warmly and briefly. When someone asks about tests or pricing, always show BOTH the individual à-la-carte tests with their prices AND the packages that include them, so they can compare. Always mention our free 15-minute consultation. Never give medical advice — for clinical questions, ask for their email so a navigator can follow up.'}
              value={settings.customInstructions}
              onChange={e => setSettings({ ...settings, customInstructions: e.target.value })} />
          </div>

          <div className="border-t border-gray-200 pt-5 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Where should leads go?</h3>
              <p className="text-sm text-gray-500">Every captured lead is pushed instantly to whatever you fill in. Fill only the ones you use — leave the rest blank and leads still land in the <b>Leads</b> tab.</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Slack — incoming webhook URL</label>
              <input className="input" placeholder={ph('slackWebhookUrl', 'https://hooks.slack.com/services/…')} value={settings.slackWebhookUrl} onChange={e => setField('slackWebhookUrl', e.target.value)} />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Telegram — bot token</label>
                <input className="input" placeholder={ph('telegramBotToken', '123456:ABC-…')} value={settings.telegramBotToken} onChange={e => setField('telegramBotToken', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Telegram — chat ID</label>
                <input className="input" placeholder="e.g. 987654321" value={settings.telegramChatId} onChange={e => setField('telegramChatId', e.target.value)} />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Discord — webhook URL</label>
              <input className="input" placeholder={ph('discordWebhookUrl', 'https://discord.com/api/webhooks/…')} value={settings.discordWebhookUrl} onChange={e => setField('discordWebhookUrl', e.target.value)} />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Any tool — generic webhook URL (we POST the lead as JSON)</label>
              <input className="input" placeholder={ph('webhookUrl', 'https://your-app.com/hooks/leads')} value={settings.webhookUrl} onChange={e => setField('webhookUrl', e.target.value)} />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">HubSpot — private-app token (creates a contact)</label>
              <input className="input" placeholder={ph('hubspotToken', 'pat-…')} value={settings.hubspotToken} onChange={e => setField('hubspotToken', e.target.value)} />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email — notification address</label>
              <input className="input" type="email" placeholder="leads@yourcompany.com" value={settings.notifyEmail} onChange={e => setField('notifyEmail', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Email needs a SendGrid key configured on the server (see setup note).</p>
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-sm font-medium text-gray-700 mb-1">WhatsApp — via Twilio</p>
              <p className="text-xs text-gray-500 mb-2">Needs a Twilio account with a WhatsApp sender (the sandbox is free to test). Fill all four:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="input" placeholder="Twilio Account SID (AC…)" value={settings.twilioSid} onChange={e => setField('twilioSid', e.target.value)} />
                <input className="input" placeholder={ph('twilioToken', 'Twilio Auth Token')} value={settings.twilioToken} onChange={e => setField('twilioToken', e.target.value)} />
                <input className="input" placeholder="From — your WhatsApp sender (+1…)" value={settings.twilioWhatsappFrom} onChange={e => setField('twilioWhatsappFrom', e.target.value)} />
                <input className="input" placeholder="To — your number (+1…)" value={settings.whatsappTo} onChange={e => setField('whatsappTo', e.target.value)} />
              </div>
            </div>

            <p className="text-xs text-gray-400">Instagram isn't a lead-delivery channel — its DMs are for customers messaging you, not for notifications. Use Slack, Telegram, or WhatsApp instead.</p>
          </div>

          <button onClick={saveSettings} disabled={savingSettings} className="btn-primary disabled:opacity-60">
            {savingSettings ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      )}

      {/* New chatbot — choose its data source (instance) */}
      {newBot && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" onClick={() => !newBot.busy && setNewBot(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-xl p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Bot size={18} /> New chatbot</h3>
              <button onClick={() => setNewBot(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input className="input mb-4" value={newBot.name} onChange={e => setNewBot(b => ({ ...b, name: e.target.value }))} placeholder="Support Bot" />

            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5"><Layers size={14} /> Data source</label>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-2">
              {[['existing', 'Existing source'], ['new', 'New source']].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setNewBot(b => ({ ...b, mode: k }))}
                  disabled={k === 'existing' && dataSources.length === 0}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${newBot.mode === k ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'} disabled:opacity-40`}>
                  {label}
                </button>
              ))}
            </div>
            {newBot.mode === 'existing' ? (
              <>
                <select className="input" value={newBot.instanceId} onChange={e => setNewBot(b => ({ ...b, instanceId: e.target.value }))}>
                  {dataSources.map(i => <option key={i.id} value={i.id}>{i.name}{i.sourceUrl ? ` — ${i.sourceUrl}` : ' — standalone'}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">This bot shares that source’s knowledge — every service on it stays in sync.</p>
              </>
            ) : (
              <>
                <input className="input mb-2" placeholder="Source name (e.g. Internal HR)" value={newBot.sourceName} onChange={e => setNewBot(b => ({ ...b, sourceName: e.target.value }))} />
                <input className="input" placeholder="https://… (optional — connect content after)" value={newBot.sourceUrl} onChange={e => setNewBot(b => ({ ...b, sourceUrl: e.target.value }))} />
                <p className="text-xs text-gray-400 mt-1">Creates a new instance — this bot won’t see your other sources’ data.</p>
              </>
            )}

            <button onClick={submitNewBot} disabled={newBot.busy} className="btn-primary w-full mt-5 flex items-center justify-center gap-2 disabled:opacity-60">
              {newBot.busy ? <Loader2 className="animate-spin" size={16} /> : <Bot size={16} />} Create chatbot
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
